import React, { Fragment, useEffect, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { useNotification } from "@hooks/useNotification";
import Button from "@components/tailwindComponents/Button";
import ButtonCloseModal from "@components/tailwindComponents/CloseModal";
import { reactMemo } from "@helpers/retyped-functions";

const widthGlobal = {
  "small": "arh-w-[36.125rem]",
  "medium": "arh-w-[40rem]",
  "large": "arh-w-[58.375rem] arh-max-w-[60rem]",
  "very-large": "arh-w-[72.5rem] arh-max-w-[90rem]",
  "extra-large": "arh-w-11/12 arh-max-w-[100rem]"
};

/**
 * @typedef {Object} ButtonOptions
 * @property {Function} click - Function to handle the action button click event.
 * @property {string} color - Color of the action button.
 * @property {string} title - Title or label for the action button.
 * @property {string=} maxWidth - Max width of the action button.
 */

/**
 * @typedef {Object} Props
 * @property {boolean} isOpen
 * @property {boolean=} isLoading
 * @property {boolean=} initializationInProgress
 * @property {string=} title
 *  @property {("none"|"simple"|"double")=} buttonType
 * -none: pas de button
 * -simple: Simple button
 * -double: Double button
 * @property {("small"|"medium"|"large"|"very-large"|"extra-large")=} widthType
 * - small : width 36.125 rem ou 578px
 * -medium: width 40 rem ou 640px
 * large: width varie entre 934px et 960px
 * very-large: width varie entre 1160px et 1440px
 * extra-large: max width est de 1600px
 * @property {ButtonOptions=} buttonOptions
 * - click: Function to handle the action button click event.
 * - color : Color of the action button.
 * -title : Title or label for the action button.
 * -maxWidth : Max width of the action button.
 * @property {ButtonOptions=} buttonCancelOptions
 * - click: Function to handle the action button click event.
 * - color : Color of the action button.
 * -title : Title or label for the action button.
 * -maxWidth : Max width of the action button.
 * @property {Function} onClickClose
 * @property {Function=} preSave
 * @property {boolean=} btnSaveDisabled
 * @property {boolean=} mustWaiting
 * @property {boolean=} withCloseCroix
 * @property {boolean=} WithSpaceLoading
 * @property {string=} descriptionSubButton
 * @property {string=} WithMarge
 * @property {JSX.Element=} customTitle
 * @property {JSX.Element=} customSubButton
 * @property {JSX.Element} children
 * @property {string=} parentDoubleBtn
 * @property {boolean=} withoutTitle
 * @property {string=} positionTitle
 * @property {boolean=} closeOnOutsideClick 
 */

/**
 * @example
 *
 * buttonOptions , buttonCancelOptions:{
 *    click: ()=>{} // Function,
 *    color: "" //string
 *    title: "",
 * }
 *
 * @param {Props} props
 * @returns
 */
function ModalIndex(props) {
  const {
    isOpen,
    widthType = "small",
    isLoading,
    buttonType = "double",
    title = "",
    customTitle = null,
    buttonOptions = {
      click: () => { },
      color: "green",
      title: "",
      maxWidth: "arh-max-w-[25rem]"
    },
    buttonCancelOptions = {
      click: () => { },
      color: "outlined",
      title: "",
      maxWidth: "arh-max-w-[11.25rem]"
    },
    parentDoubleBtn = "arh-justify-center",
    onClickClose = () => { },
    children,
    btnSaveDisabled = false,
    mustWaiting,
    descriptionSubButton = "",
    customSubButton = null,
    withCloseCroix = true,
    WithMarge = "",
    WithSpaceLoading = true,
    preSave,
    withoutTitle = false,
    positionTitle="",
    initializationInProgress = false,
    closeOnOutsideClick = true, 
    // showClickClose = true,
  } = props;

  const { showInfo } = useNotification();
  const [isWaiting, setIsWaiting] = useState(false);

  const requestLoading = () => {
    showInfo("request_being_executed");
  };

  const handleSaveClick = async () => {
    let allOk = true;
    if (preSave) {
      allOk = preSave();
    }
    if (allOk) {
      if (isLoading === undefined && mustWaiting === undefined) {
        try {
          setIsWaiting(true);
          await buttonOptions.click();
        } finally {
          setIsWaiting(false);
        }
      } else {
        buttonOptions.click();
      }
    }
  };

  useEffect(() => {
    if (isOpen && isWaiting) {
      setIsWaiting(false);
    }
  }, [isOpen]);

  function TypeButton() {
    switch (buttonType) {
      case "none":
        return null;
      case "simple": {
        const isBtnDisabled = btnSaveDisabled || isLoading || mustWaiting || isWaiting || initializationInProgress;
        return (
          <Button
            variant={buttonOptions?.color}
            onClick={handleSaveClick}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.stopPropagation();
                e.preventDefault();
              }
            }}
            disabled={isBtnDisabled}
            wait={isLoading || isWaiting || initializationInProgress}
            className={`${(mustWaiting || isWaiting || initializationInProgress) && "arh-cursor-wait"
              } arh-mx-auto ${buttonOptions?.maxWidth}`}
          >
            {buttonOptions?.title || ""}
          </Button>
        );
      }
      case "double": {
        const isBtnDisabled = btnSaveDisabled || isLoading || mustWaiting || isWaiting || initializationInProgress;
        return (
          <div
            className={`arh-flex arh-w-full arh-items-center arh-space-x-8 mobile-768:arh-flex-col mobile-768:arh-space-x-0 mobile-768:arh-space-y-4 ${parentDoubleBtn}`}
          >
            <Button
              variant={buttonCancelOptions?.color}
              onClick={(isLoading || isWaiting) ? requestLoading : buttonCancelOptions?.click}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.stopPropagation();
                  e.preventDefault();
                }
              }}
              className={`!arh-w-2/5 ${buttonCancelOptions?.maxWidth} mobile-768:!arh-w-full mobile-768:arh-max-w-full`}
            >
              {buttonCancelOptions?.title || ""}
            </Button>
            <Button
              variant={buttonOptions?.color}
              onClick={handleSaveClick}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.stopPropagation();
                  e.preventDefault();
                }
              }}
              className={`!arh-w-2/5 ${buttonOptions?.maxWidth} mobile-768:!arh-w-full mobile-768:arh-max-w-full ${(mustWaiting || isWaiting || initializationInProgress) && "arh-cursor-wait"
                }`}
              disabled={isBtnDisabled}
              wait={isLoading || isWaiting || initializationInProgress}
            >
              {buttonOptions?.title || ""}
            </Button>
          </div>
        );
      }
    }
  }

  return (
    isOpen && (
      <Transition
        appear
        show={isOpen}
        as={Fragment}
      >
        <Dialog
          as="div"
          className={`arh-relative arh-z-50`}
          onClose={closeOnOutsideClick ? onClickClose : () => {}}
        >
          <Transition.Child
            as={Fragment}
            enter="arh-ease-out arh-duration-300"
            enterFrom="arh-opacity-0"
            enterTo="arh-opacity-100"
            leave="arh-ease-in arh-duration-200"
            leaveFrom="arh-opacity-100"
            leaveTo="arh-opacity-0"
          >
            <div className="arh-fixed arh-inset-0 arh-bg-modalBackdrop arh-bg-opacity-90" />
          </Transition.Child>
          <div className="arh-fixed arh-inset-0 arh-overflow-y-auto">
            <div className="arh-flex arh-min-h-full arh-items-center arh-justify-center arh-p-2 arh-text-center">
              <Transition.Child
                as={Fragment}
                enter="arh-ease-out arh-duration-300"
                enterFrom="arh-opacity-0 arh-scale-95"
                enterTo="arh-opacity-100 arh-scale-100"
                leave="arh-ease-in arh-duration-200"
                leaveFrom="arh-opacity-100 arh-scale-100"
                leaveTo="arh-opacity-0 arh-scale-95"
              >
                <Dialog.Panel
                  className={`arh-relative ${widthGlobal[widthType]} ${WithMarge} wadeDatePicker arh-transform arh-rounded-lg arh-bg-white arh-p-20 arh-py-11 arh-align-middle arh-shadow-xl arh-transition-all mobile-768:arh-p-4`}
                >
                  <div className={`arh-flex arh-flex-col ${!withoutTitle && "arh-space-y-4"} arh-py-0`}>
                    {withCloseCroix ? (
                      <div className={`arh-w-full ${withoutTitle && "arh-h-0"}`}>
                        <Dialog.Title
                          as="h3"
                          className={`${withoutTitle ? "arh-mt-0" : "arh-mt-8"} ${positionTitle} arh-text-center arh-font-poppins arh-text-xl arh-font-semibold arh-text-black mobile-768:arh-text-base`}
                        >
                          {customTitle || title}
                        </Dialog.Title>
                        {/* {showClickClose && ( */}
                        <ButtonCloseModal
                          onClick={(isLoading || isWaiting) ? requestLoading : onClickClose}
                        />
                        {/* )} */}
                      </div>
                    ) : (
                      ""
                    )}
                    <div>{children}</div>
                  </div>
                  {WithSpaceLoading ? (
                    <div className="arh-mt-12 arh-flex arh-flex-col arh-space-y-2">
                      {TypeButton()}
                      {descriptionSubButton && (
                        <span className="arh-block arh-font-poppins arh-text-xs arh-font-medium arh-text-black">
                          {descriptionSubButton}
                        </span>
                      )}
                      {customSubButton && (
                        <div className="arh-mt-2">{customSubButton}</div>
                      )}
                    </div>
                  ) : (
                    ""
                  )}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    )
  );
}
export default reactMemo(ModalIndex);
